import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import './App.css';

function responseFilename(response) {
  const disposition = response.headers.get('Content-Disposition') || '';
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) return decodeURIComponent(utf8Match[1]);
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
  return plainMatch ? plainMatch[1] : 'NeuLogic-License.lic';
}

async function responseError(response) {
  try {
    const data = await response.json();
    return data.error || 'The license could not be generated.';
  } catch {
    return 'The license could not be generated. Please try again.';
  }
}

export default function Activate() {
  const [searchParams] = useSearchParams();
  const checkoutSessionId = searchParams.get('session_id') || '';
  const [machineCode, setMachineCode] = useState('');
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const machineCodeRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.add('activation-route');
    return () => document.documentElement.classList.remove('activation-route');
  }, []);

  useEffect(() => {
    const textarea = machineCodeRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.max(132, textarea.scrollHeight)}px`;
  }, [machineCode]);

  const activate = async event => {
    event.preventDefault();
    const normalizedCode = machineCode.trim();
    if (!normalizedCode) {
      setStatus({ type: 'error', message: 'Enter the machine code shown in the application.' });
      return;
    }

    setStatus({ type: 'working', message: 'Verifying the purchase and creating the license…' });

    try {
      const response = await fetch('/api/activate.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          checkout_session_id: checkoutSessionId,
          machine_code: normalizedCode
        })
      });

      if (!response.ok) {
        throw new Error(await responseError(response));
      }

      const filename = responseFilename(response);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);

    setStatus({
      type: 'success',
      message: 'License created successfully. The download should begin automatically.\nReturn to the application, click Import License, and select the downloaded license file.'
    });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    }
  };

  return (
    <main className="activation-page">
      <section className="activation-panel" aria-labelledby="activation-title">
        <div className="activation-heading">
          <div className="activation-lock" aria-hidden="true">🔐</div>
          <div>
            <h1 id="activation-title">Activate NeuLogic Software</h1>
            <p>Create the license for this computer.</p>
          </div>
        </div>

        {!checkoutSessionId ? (
          <div className="activation-empty">
            <h2>Purchase confirmation required</h2>
            <p>
              After payment, Stripe returns to this page with the purchase confirmation
              needed to create the license.
            </p>
            <div className="activation-actions">
              <Link className="card-btn activation-primary" to="/">
                Home
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={activate} className="activation-form">
            <ol className="activation-steps">
              <li>Open the downloaded application and click the <strong>Activate License</strong> button.</li>
              <li>Click the <strong>Copy Machine Code</strong> button.</li>
              <li>Paste the copied code into the <strong>Machine Code</strong> input field below.</li>
              <li>Click the <strong>Activate and Download</strong> button.</li>
            </ol>

            <label htmlFor="machine-code">Machine Code</label>
            <textarea
              ref={machineCodeRef}
              id="machine-code"
              value={machineCode}
              onChange={event => setMachineCode(event.target.value)}
              placeholder="Paste the complete machine code"
              rows="5"
              spellCheck="false"
              autoComplete="off"
              disabled={status.type === 'working'}
              required
            />
            <p className="activation-privacy">
              The code contains hashed computer identifiers. It does not contain passwords,
              vault contents, or readable hardware serial numbers.
            </p>

            <div className="activation-submit">
              <button
                className="card-btn activation-primary"
                type="submit"
                disabled={status.type === 'working'}
              >
                {status.type === 'working' ? 'Creating License…' : 'Activate and Download'}
              </button>
            </div>

            {status.type !== 'idle' && (
              <div className={`activation-status activation-status-${status.type}`} role="status">
                {status.message}
              </div>
            )}
          </form>
        )}
      </section>
    </main>
  );
}